import type { GameSide } from '../chatv1/transport'

/**
 * The gomoku board's geometry, apart from any drawing so it can be tested: points by name ("h8": a column letter a–o
 * from the left, a row 1–15 from the bottom — the service's own names, chat lambda/gomoku.ts), the stones a move
 * list has laid, and where a point is on the 3D board.
 */

export const SIZE = 15
const COLS = 'abcdefghijklmno'
const POINT = /^([a-o])(1[0-5]|[1-9])$/

/** The five marked points of a 15×15 board: the centre and the four 4-4 points. */
export const STAR_POINTS: readonly string[] = ['d4', 'l4', 'h8', 'd12', 'l12']

/** [column, row], both from 0, or null. */
export function parsePoint(p: string): [number, number] | null {
  const m = POINT.exec(p)
  return m ? [COLS.indexOf(m[1]!), Number(m[2]) - 1] : null
}

function pointName(col: number, row: number): string {
  return `${COLS[col]}${row + 1}`
}

/** Every point, top row first and left to right — the order a reader (and a screen reader) meets them. */
export function allPoints(): string[] {
  const out: string[] = []
  for (let row = SIZE - 1; row >= 0; row--) for (let col = 0; col < SIZE; col++) out.push(pointName(col, row))
  return out
}

export function columnLetter(col: number): string {
  return COLS[col]!
}

/** The stones on the board, by point: Black laid the first, White the second, and so on. */
export function stonesOf(moves: readonly string[]): Map<string, { side: GameSide; n: number }> {
  const out = new Map<string, { side: GameSide; n: number }>()
  moves.forEach((p, i) => {
    if (!out.has(p)) out.set(p, { side: i % 2 === 0 ? 'b' : 'w', n: i + 1 })
  })
  return out
}

/**
 * 3D: one board cell = 1 world unit, the board in the x–z plane centred on the origin; row 1 nearest the camera
 * (+z), column a on the left (−x).
 */
export function pointToWorld(p: string): [number, number] | null {
  const at = parsePoint(p)
  if (!at) return null
  const mid = (SIZE - 1) / 2
  return [at[0] - mid, mid - at[1]]
}

/** The point under a spot on the board, when the spot is near enough to it to mean it; else null. */
export function worldToPoint(x: number, z: number): string | null {
  const mid = (SIZE - 1) / 2
  const col = Math.round(x + mid)
  const row = Math.round(mid - z)
  if (col < 0 || col >= SIZE || row < 0 || row >= SIZE) return null
  if (Math.hypot(x - (col - mid), z - (mid - row)) > 0.5) return null
  return pointName(col, row)
}
